/**
 * Application Configuration
 *
 * This file centralizes all environment variables and configuration settings.
 *
 * Configuration Priority (highest to lowest):
 * 1. Runtime config (window.__RUNTIME_CONFIG__) - from Docker environment variables
 * 2. Build-time config (process.env.REACT_APP_*) - from .env files
 * 3. Dynamic URL construction (when REACT_APP_USE_DYNAMIC_API_URL=true)
 * 4. Default values
 *
 * Dynamic API URL:
 * When REACT_APP_USE_DYNAMIC_API_URL=true, the API URL is automatically constructed
 * from the browser's current origin. This allows the app to work on any host/IP/domain
 * without hardcoding the API URL.
 *
 * Usage:
 * import config from '@/config';
 * const apiUrl = config.api.baseURL;
 */

import { getApiBaseUrl } from './utils/dynamicApiUrl';

// Get runtime config if available (set by docker-entrypoint.sh)
const runtimeConfig = typeof window !== 'undefined' ? window.__RUNTIME_CONFIG__ : null;

// Log configuration source
if (runtimeConfig) {
  console.log('[Config] Using runtime configuration from Docker environment variables');
} else {
  console.log('[Config] Using build-time configuration from .env files');
}

const config = {
  // Environment
  env: runtimeConfig?.env || process.env.REACT_APP_ENV || 'development',
  isDevelopment: (runtimeConfig?.env || process.env.REACT_APP_ENV) === 'development',
  isProduction: (runtimeConfig?.env || process.env.REACT_APP_ENV) === 'production',

  // Application Info
  app: {
    name: runtimeConfig?.app?.name || process.env.REACT_APP_NAME || 'QLTT BTL86',
    version: runtimeConfig?.app?.version || process.env.REACT_APP_VERSION || '1.0.0',
    tutorialUrl: runtimeConfig?.app?.tutorialUrl || process.env.REACT_APP_TUTORIAL_URL || 'http://doc.duallmaster.com/',
    qlrvWebUrl: runtimeConfig?.app?.qlrvWebUrl || process.env.REACT_APP_QLRV_WEB || '',
    companyName: runtimeConfig?.app?.companyName || process.env.REACT_APP_COMPANY_NAME || 'BTL86',
    copyrightYear: runtimeConfig?.app?.copyrightYear || process.env.REACT_APP_COPYRIGHT_YEAR || '2025',
    supportEmail: runtimeConfig?.app?.supportEmail || process.env.REACT_APP_SUPPORT_EMAIL || 'support@example.com',
    supportPhone: runtimeConfig?.app?.supportPhone || process.env.REACT_APP_SUPPORT_PHONE || '+84 123 456 789',
  },

  // API Configuration
  api: {
    // Use dynamic URL construction when enabled, otherwise fall back to explicit config
    baseURL: getApiBaseUrl(),
    timeout: runtimeConfig?.api?.timeout || parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10),
    // Additional API settings for dynamic URL construction
    port: runtimeConfig?.api?.port || process.env.REACT_APP_API_PORT || null,
    portOffset: runtimeConfig?.api?.portOffset || parseInt(process.env.REACT_APP_API_PORT_OFFSET || '2000', 10),
    path: runtimeConfig?.api?.path || process.env.REACT_APP_API_PATH || '',
    useDynamicUrl: runtimeConfig?.api?.useDynamicUrl ?? (process.env.REACT_APP_USE_DYNAMIC_API_URL === 'true'),
  },

  // Feature Flags
  features: {
    enableDebug: runtimeConfig?.features?.enableDebug ?? (process.env.REACT_APP_ENABLE_DEBUG === 'true'),
    enableMockAPI: runtimeConfig?.features?.enableMockAPI ?? (process.env.REACT_APP_ENABLE_MOCK_API === 'true'),
  },

  // VNeID Integration
  vneid: {
    apiURL: runtimeConfig?.vneid?.apiUrl || process.env.REACT_APP_VNEID_API_URL || '',
    clientId: runtimeConfig?.vneid?.clientId || process.env.REACT_APP_VNEID_CLIENT_ID || '',
    redirectURL: runtimeConfig?.vneid?.redirectUrl || process.env.REACT_APP_VNEID_REDIRECT_URL || '',
  },

  // System Settings
  system: {
    maxFileUploadSize: runtimeConfig?.system?.maxFileUploadSize || parseInt(process.env.REACT_APP_MAX_FILE_UPLOAD_SIZE || '10485760', 10),
    sessionTimeout: runtimeConfig?.system?.sessionTimeout || parseInt(process.env.REACT_APP_SESSION_TIMEOUT || '3600000', 10),
  },

  // API Endpoints (relative to baseURL)
  endpoints: {
    // Authentication
    auth: {
      login: '/login',
      logout: '/logout',
      refresh: '/refresh',
      me: '/me',
    },
    // Two-Factor Authentication
    twoFactor: {
      sendOtp: '/TwoFactor/send-otp',
      enable: '/TwoFactor/enable',
      disable: '/TwoFactor/disable',
      status: '/TwoFactor/status',
    },
    // Dashboard
    dashboard: {
      statistics: '/dashboard',
    },
    // License Management (Module 2)
    license: {
      units: '/license/units',
      issuance: '/license/issuance',
      monitoring: '/license/monitoring',
      renewal: '/license/renewal',
      accounts: '/license/accounts',
      version: '/license/version',
    },
    // Catalog Management (Module 3)
    catalog: {
      organizations: '/catalog/organizations',
      positions: '/catalog/positions',
      identification: '/catalog/identification',
      guestTypes: '/catalog/guest-types',
      vehicleTypes: '/catalog/vehicle-types',
      entryReasons: '/catalog/entry-reasons',
    },
    // Positions Management
    positions: {
      init: '/positions/init',
      list: '/positions',
      create: '/positions',
      update: '/positions',
      delete: '/positions',
      getById: '/positions',
    },
    // Visit Types Management
    visitTypes: {
      init: '/visit-types/init',
      list: '/visit-types',
      create: '/visit-types',
      update: '/visit-types',
      delete: '/visit-types',
      getById: '/visit-types',
    },
    // Officer Groups Management
    officerGroups: {
      list: '/officer-groups',
      create: '/officer-groups',
      update: '/officer-groups',
      delete: '/officer-groups',
      getById: '/officer-groups',
    },
    // Visit Type Categories Management
    visitTypeCategories: {
      list: '/visit-type-categories',
      create: '/visit-type-categories',
      update: '/visit-type-categories',
      delete: '/visit-type-categories',
      getById: '/visit-type-categories',
    },
    // User Management (Module 4)
    users: {
      init: '/users/init',
      list: '/users',
      create: '/users',
      update: '/users',
      delete: '/users',
      accounts: '/users/accounts',
      roles: '/users/roles',
      authorization: '/users/authorization',
      logs: '/users/logs',
      mfa: '/users/mfa',
      security: '/users/security',
    },
    // Roles Management
    roles: {
      init: '/roles/init',
      list: '/roles',
      create: '/roles',
      update: '/roles',
      delete: '/roles',
      getById: '/roles',
    },
    // Units Management
    units: {
      init: '/units/init',
      list: '/units',
      create: '/units',
      update: '/units',
      delete: '/units',
      getById: '/units',
    },
    // Subordinate Levels Management (Cấp trực thuộc)
    subordinateLevels: {
      list: '/subordinate-levels',
      create: '/subordinate-levels',
      update: '/subordinate-levels',
      delete: '/subordinate-levels',
      getById: '/subordinate-levels',
    },
    // Vehicles Management
    vehicles: {
      init: '/vehicles/init',
      list: '/vehicles',
      create: '/vehicles',
      update: '/vehicles',
      delete: '/vehicles',
      getById: '/vehicles',
    },
    // Vehicle Types Management
    vehicleTypes: {
      list: '/vehicle-types',
      create: '/vehicle-types',
      update: '/vehicle-types',
      delete: '/vehicle-types',
      getById: '/vehicle-types',
    },
    // Entry/Exit Reasons Management
    entryExitReasons: {
      init: '/entry-exit-reasons/init',
      list: '/entry-exit-reasons',
      create: '/entry-exit-reasons',
      update: '/entry-exit-reasons',
      delete: '/entry-exit-reasons',
      getById: '/entry-exit-reasons',
      bulkDelete: '/entry-exit-reasons/bulk-delete',
    },
    // Entry/Exit Statuses Management
    entryExitStatuses: {
      list: '/entry-exit-statuses',
      create: '/entry-exit-statuses',
      update: '/entry-exit-statuses',
      delete: '/entry-exit-statuses',
      getById: '/entry-exit-statuses',
    },
    // Licenses Management
    licenses: {
      init: '/licenses/init',
      list: '/licenses',
      create: '/licenses',
      update: '/licenses',
      delete: '/licenses',
      getById: '/licenses',
      import: '/licenses/import',
      exportExcel: '/licenses/export',
      exportPdf: '/licenses/export/pdf',
      importTemplate: '/licenses/template',
    },
    // Devices Management
    devices: {
      init: '/devices/init',
      list: '/devices',
      create: '/devices',
      update: '/devices',
      delete: '/devices',
      getById: '/devices',
      import: '/devices/import',
      exportExcel: '/devices/export/excel',
      exportPdf: '/devices/export/pdf',
      importTemplate: '/devices/import/template',
    },
    // Events Management
    events: {
      init: '/events/init',
      list: '/events',
      statistics: '/events/statistics',
      create: '/events',
      update: '/events',
      delete: '/events',
      getById: '/events',
      exportExcel: '/events/export/excel',
      exportPdf: '/events/export/pdf',
    },
    // Backup & Recovery (Module 5)
    backup: {
      init: '/backups/init',
      getConfig: '/units',  // GET /units/{id}/backup-config
      sync: '/backup/sync',
      central: '/backup/central',
      unit: '/backup/unit',
      platform: '/backup/platform',
      logs: '/backup/logs',
    },
    // Backup Logs Management
    backupLogs: {
      list: '/backup-logs',
      delete: '/backup-logs',
      bulkDelete: '/backup-logs/bulk-delete',
      download: '/backup-logs',
    },
    // Restore Logs Management
    restoreLogs: {
      list: '/restore-logs',
    },
    // Reports (Module 6)
    reports: {
      init: '/reports/init',
      access: '/reports/access',
      accessExport: '/reports/access/export',
      device: '/reports/device',
      deviceExport: '/reports/device/export',
      deviceRefresh: '/reports/device/refresh',
      devices: '/reports/devices',
      database: '/reports/database',
      violation: '/reports/violation',
      violationExport: '/reports/violation/export',
      violations: '/reports/violations',
    },
    // Database Analysis (Reports Module)
    databaseAnalyses: {
      init: '/database-analyses/init',
      list: '/database-analyses',
      create: '/database-analyses',
      update: '/database-analyses',
      delete: '/database-analyses',
      getById: '/database-analyses',
      exportExcel: '/database-analyses/export/excel',
      exportPdf: '/database-analyses/export/pdf',
      usageReport: '/database-analyses/usage-report',
      usageReportExportExcel: '/database-analyses/usage-report/export/excel',
      usageReportExportPdf: '/database-analyses/usage-report/export/pdf',
    },
    // Violation Logs (Reports Module)
    violationLogs: {
      init: '/violation-logs/init',
      list: '/violation-logs',
      statistics: '/violation-logs/statistics',
      create: '/violation-logs',
      update: '/violation-logs',
      delete: '/violation-logs',
      getById: '/violation-logs',
      exportExcel: '/violation-logs/export/excel',
      exportPdf: '/violation-logs/export/pdf',
    },
    // System Configuration (Module 7)
    config: {
      files: '/config/files',
      notifications: '/config/notifications',
      logs: '/config/logs',
    },
    // VNeID Query Monitoring
    vneidQueryMonitoring: {
      connectionStatus: '/vneid/query-monitoring/connection-status',
      dashboard: '/vneid/query-monitoring/dashboard',
      history: '/vneid/query-monitoring/history',
      statistics: '/vneid/query-monitoring/statistics',
      volume: '/vneid/query-monitoring/volume',
      typeDistribution: '/vneid/query-monitoring/type-distribution',
      exportMonthly: '/vneid/query-monitoring/export/monthly',
      exportQuarterly: '/vneid/query-monitoring/export/quarterly',
    },
    // VNeID Integration (Module 8)
    vneidIntegration: {
      sso: '/vneid/sso',
      accounts: '/vneid/accounts',
      queries: '/vneid/queries',
      sdk: '/vneid/sdk',
      monitoring: '/vneid/monitoring',
      events: '/events',
    },
    // VNeID SDK Management
    vneidSdk: {
      status: '/vneid/sdk/status',
      download: '/vneid/sdk/download',
      downloadFile: '/vneid/sdk/download/file',
      release: '/vneid/sdk/release',
      forceUpdate: '/vneid/sdk/force-update',
      blockOutdated: '/vneid/sdk/block-outdated',
      generateKey: '/vneid/sdk/api-keys/generate',
      revokeKey: '/vneid/sdk/api-keys/{id}/revoke',
      updateIntegration: '/vneid/sdk/integrations/{id}/update',
      logs: '/vneid/sdk/integrations/{id}/logs',
      integrations: '/vneid/sdk/integrations',
      versionHistory: '/vneid/sdk/version-history',
    },
    // Settings Management
    settings: {
      list: '/settings',
      update: '/settings',
    },
    // System Logs Management
    systemLogs: {
      init: '/system-logs/init',
      list: '/system-logs',
    },
    // Alert Configurations Management
    alertConfigurations: {
      init: '/api/alert-configurations/init',
      list: '/api/alert-configurations',
      getById: '/api/alert-configurations/{id}',
      create: '/api/alert-configurations',
      update: '/api/alert-configurations/{id}',
      delete: '/api/alert-configurations/{id}',
      toggleActive: '/api/alert-configurations/{id}/toggle-active',
    },
    // Approval Accounts Management
    approvalAccounts: {
      init: '/approval-accounts/init',
      list: '/approval-accounts',
      delete: '/approval-accounts',
      unassign: '/approval-accounts/un-assign',
      assign: '/approval-accounts/assign',
    },
    // VNeID Accounts Management
    vneidAccounts: {
      list: '/vneid-accounts',
      create: '/vneid-accounts',
      update: '/vneid-accounts',
      delete: '/vneid-accounts',
      getById: '/vneid-accounts',
      toggleStatus: '/vneid-accounts/{id}/toggle-status',
      regenerateKey: '/vneid-accounts/{id}/regenerate-key',
      report: '/vneid-accounts/report',
      exportExcel: '/vneid-accounts/report/export/excel',
      exportPdf: '/vneid-accounts/report/export/pdf',
      authUrl: '/vneid-accounts/auth/url',
      statistics: '/vneid-accounts/statistics',
    },
    // Account Management (Password Policy)
    accountManagement: {
      unlockAccount: '/accountmanagement/unlock-account',
      lockedAccounts: '/accountmanagement/locked-accounts',
      isLocked: '/accountmanagement/is-locked',
      changePasswordExpired: '/accountmanagement/change-password-expired',
    },
  },
};

// Freeze the config object to prevent modifications
Object.freeze(config);
Object.freeze(config.app);
Object.freeze(config.api);
Object.freeze(config.features);
Object.freeze(config.vneid);
Object.freeze(config.system);
Object.freeze(config.endpoints);

// Note: app object properties are already frozen by Object.freeze(config.app)

export default config;
